import React, { useMemo, useState } from 'react';

import {
	Accordion,
	AccordionButton,
	AccordionIcon,
	AccordionItem,
	AccordionPanel,
	useDisclosure,
} from '@chakra-ui/react';
import { FaGlobe } from '@react-icons/all-files/fa/FaGlobe';
import { FaRegCalendarAlt } from '@react-icons/all-files/fa/FaRegCalendarAlt';
import { FaRegClock } from '@react-icons/all-files/fa/FaRegClock';
import dayjs from 'dayjs';
import { Link as GatsbyLink, graphql, HeadProps, PageProps } from 'gatsby';
import { StaticImage } from 'gatsby-plugin-image';

import { DigitalizationAttendeesForm } from '../components/AttendeesForm/DigitalizationAttendeesForm';
import { Button } from '../components/Button/Button';
import { Drawer } from '../components/Drawer/Drawer';
import { DynamicSection } from '../components/DynamicSection/DynamicSection';
import { ImageRightHero } from '../components/ImageRightHero/ImageRightHero';
import { Layout } from '../components/Layout/Layout';
import { MarkdownRaw } from '../components/MarkdownRaw';
import { SearchEngineOptimization } from '../components/SearchEngineOptimization/SearchEngineOptimization';
import { SpeculationRules } from '../components/SpeculationRules';
import { useCmsData } from '../hooks/use-cms-data';
import { useMail } from '../hooks/use-mail';
import { useSettings } from '../hooks/use-settings';
import { useToast } from '../hooks/use-toast';
import { SUBJECT_QUERY_PARAM } from '../misc/contact';
import { isFutureAppointment, sortAppointments } from '../misc/courses';
import { BreadCrumbItem, Url } from '../misc/enums';
import { FaqSchema } from '../misc/schema';
import { formatCurrency, formatDate, pluralizeIfRequired } from '../misc/utilities';
import { Attendee } from '../models/Attendee';
import { Digitalization } from '../types/digitalization';

const EVENTS_SECTION_ID = 'veranstaltungen';

type Data = {
	digitalizationPage: { fileAbsolutePath: string; frontmatter: Digitalization };
};

type PageContext = {
	parent: {
		frontmatter: {
			title: string;
			slug: string;
		};
	} | null;
};

const DigitalizationTemplate = ({ data, pageContext }: PageProps<Data, PageContext>) => {
	const {
		digitalizationPage: { frontmatter: digitalizationPage },
	} = data;
	const { digitalizationPages } = useCmsData();

	const events = useMemo(() => {
		// we want to show the events of the sub-pages on the parent page
		const isParentPage = !pageContext.parent;
		const subEvents = isParentPage
			? digitalizationPages
					.filter(({ node: { frontmatter } }) => frontmatter.parent === digitalizationPage.uuid)
					.map(({ node: { frontmatter } }) => frontmatter.events)
					.flat()
					.filter(Boolean)
			: [];
		const allEvents = [...(digitalizationPage.events ?? []), ...subEvents];
		const futureEvents = sortAppointments(allEvents.filter(isFutureAppointment));
		return futureEvents;
	}, [digitalizationPage.events, digitalizationPage.uuid, digitalizationPages, pageContext.parent]);

	const renderButtons = useMemo(() => {
		const buttons = [
			<Button
				key="contact"
				as={GatsbyLink}
				to={`/${Url.CONTACT}/?${SUBJECT_QUERY_PARAM}=${digitalizationPage.title}`}
			>
				Kostenlos anfragen
			</Button>,
		];

		if (!digitalizationPage.hideEvents && events.length > 0) {
			buttons.push(
				<Button
					key="veranstaltungen"
					as={GatsbyLink}
					to={`#${EVENTS_SECTION_ID}`}
					variant="outline"
				>
					Zu den Veranstaltungen
				</Button>,
			);
		}
		return buttons;
	}, [digitalizationPage.hideEvents, digitalizationPage.title, events.length]);

	return (
		<Layout pt={0}>
			<ImageRightHero
				heroImage={digitalizationPage.heroImage as any}
				title={digitalizationPage.title}
				description={digitalizationPage.intro}
				renderButtons={renderButtons}
				className="pb-12 pt-8 lg:pb-24 lg:pt-24 xl:pt-40"
			/>

			{(digitalizationPage.sections ?? []).map((section, idx) => {
				return <DynamicSection key={idx} section={section} />;
			})}

			{!digitalizationPage.hideEvents && (
				<section
					id={EVENTS_SECTION_ID}
					className="relative mb-8 overflow-hidden rounded-xl py-16 lg:mx-8"
				>
					<div className="container">
						<h2 className="text-balance text-center text-3xl font-semibold text-secondary-700">
							Veranstaltungen
						</h2>
						{events.length > 0 ? (
							<Events events={events} className="mt-5 w-full" />
						) : (
							<p className="mt-8 text-center text-lg font-semibold text-secondary-700">
								Es sind derzeit keine Veranstaltungen geplant.
							</p>
						)}
					</div>
					<div className="absolute inset-x-0 top-0 -z-20 h-full opacity-35">
						<StaticImage
							src="../../static/images/hero_background.png"
							alt=""
							className="h-full w-full"
						/>
					</div>
				</section>
			)}
		</Layout>
	);
};

export default DigitalizationTemplate;

export const Head = ({ data, pageContext }: HeadProps<Data, PageContext>) => {
	const {
		digitalizationPage: { frontmatter: digitalizationPage },
	} = data;
	const { parent } = pageContext;

	const path = `/${Url.DIGITALIZATION}/${parent?.frontmatter.slug ? `${parent.frontmatter.slug}/` : ''}${digitalizationPage.slug}/`;
	const breadcrumbItems = [
		BreadCrumbItem.HOME,
		BreadCrumbItem.DIGITALIZATION,
		parent
			? {
					title: parent.frontmatter.title,
					to: `/${Url.DIGITALIZATION}/${parent.frontmatter.slug}/`,
				}
			: undefined,
		{
			title: digitalizationPage.title,
			to: path,
		},
	].filter(Boolean) as Array<{
		title: string;
		to: string;
	}>;

	const seoImage = digitalizationPage.heroImage?.image?.childImageSharp?.seoImage;
	const isSvg = digitalizationPage.heroImage?.image?.extension === 'svg';
	const imagePath = isSvg
		? digitalizationPage.heroImage?.image?.publicURL
		: seoImage
			? seoImage.images.fallback?.src
			: undefined;

	return (
		<>
			<SpeculationRules />
			<SearchEngineOptimization
				title={digitalizationPage.seo.title}
				description={digitalizationPage.seo.description}
				path={path}
				imagePath={imagePath}
				breadcrumbItems={breadcrumbItems}
			/>
			<FaqSchema sections={digitalizationPage.sections as any} />
		</>
	);
};

type EventsProps = {
	events: Digitalization['events'];
	className?: string;
};

const MAX_ITEMS_TO_SHOW = 6;

function Events({ events, className }: EventsProps) {
	const {
		general: { vat },
	} = useSettings();
	const [selectedEvent, setSelectedAppointment] = useState<Digitalization['events'][number] | null>(
		null,
	);
	const { isSending, sendMail } = useMail();
	const [isShowAll, setIsShowAll] = useState(false);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { showErrorToast, showSuccessToast } = useToast();

	const handleClickShowAll = () => {
		setIsShowAll(true);
	};

	const handleClickAttend = (event: Digitalization['events'][number]) => {
		onOpen();
		setSelectedAppointment(event);
	};

	const handleAttend = async (attendees: Attendee[]) => {
		if (!selectedEvent) {
			return;
		}
		try {
			await sendMail({
				subject: `Anmeldung für Veranstaltung: ${selectedEvent.title}`,
				message: createEmailMessage({ event: selectedEvent, attendees }),
				replyToMail: '',
				recaptchaToken: '',
				verifyRecaptcha: false,
			});
			showSuccessToast({
				title: 'Anmeldung erfolgreich',
				message: 'Wir haben Ihre Anmeldung erhalten. Wir werden uns in Kürze bei Ihnen melden.',
			});
			window?.plausible?.('event_booked', {
				props: {
					event_uuid: selectedEvent.uuid,
					event_title: selectedEvent.title,
					attendee_count: String(attendees.length),
					total_price: String((selectedEvent.price ?? 0) * attendees.length),
				},
			});
			onClose();
			setSelectedAppointment(null);
		} catch (error) {
			showErrorToast({
				title: 'Fehler beim Anmelden',
				message: 'Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
			});
		}
	};

	const filteredEvents = useMemo(() => {
		if (isShowAll) {
			return events;
		}

		return events.slice(0, MAX_ITEMS_TO_SHOW);
	}, [events, isShowAll]);

	return (
		<div className={className}>
			<p className="text-lg font-semibold text-secondary-700">
				{events.length === 0
					? 'Keine Veranstaltungen verfügbar'
					: `${events.length} ${pluralizeIfRequired(events.length === 1, 'Veranstaltung', 'Veranstaltungen')} verfügbar`}
			</p>
			<Accordion allowMultiple w="full" gap={5} mt={3}>
				{filteredEvents.map((event) => {
					const totalDays = dayjs(event.end).diff(event.start, 'day') + 1;
					const dates = Array.from({ length: totalDays }, (_, i) =>
						dayjs(event.start).add(i, 'day'),
					);

					return (
						<AccordionItem
							key={event.uuid}
							my={3}
							border="none"
							rounded="2xl"
							bg="white"
							pl={16}
							pr={8}
							py={5}
							position="relative"
							overflow="hidden"
						>
							<div className="flex items-center justify-between">
								<div className="flex flex-col-reverse gap-2 lg:flex-row lg:gap-12">
									<div className="flex items-center gap-2 text-secondary-700">
										<FaRegCalendarAlt />
										<p className="truncate text-lg font-semibold">{formatDate(event.start)}</p>
									</div>
									<p className="text-lg font-semibold text-secondary-700">{event.title}</p>
								</div>

								<div className="flex flex-col-reverse gap-2 lg:flex-row lg:gap-6">
									<AccordionButton
										py={1}
										px={5}
										rounded="full"
										className="text-lg font-semibold text-primary-600"
									>
										<span>Details</span>
										<AccordionIcon w={8} h={8} />
									</AccordionButton>
									<Button
										onClick={() => handleClickAttend(event)}
										data-start={event.start}
										data-end={event.end}
										className="shrink-0"
										variant="outline"
										rounded="full"
									>
										Teilnehmen
									</Button>
								</div>
							</div>
							<AccordionPanel className="px-0">
								<div className="flex flex-col gap-8 py-6 xl:flex-row xl:gap-12">
									<div className="flex shrink-0 flex-col gap-10 md:flex-row md:gap-10 xl:flex-col">
										<div>
											<p className="text-sm font-bold tracking-wide text-secondary-700">
												Buchungskosten
											</p>
											{event.price > 0 ? (
												<div className="mt-1 flex flex-col gap-1 text-secondary-700">
													<div className="flex items-end gap-2 font-bold">
														<span className="text-2xl tabular-nums leading-none">
															{formatCurrency(event.price)}€
														</span>
														<span className="text-md leading-none">Netto</span>
													</div>
													<div className="flex items-end gap-1 font-bold opacity-80">
														<span className="text-md tabular-nums leading-none">
															{formatCurrency(event.price * ((100 + vat) / 100))}€
														</span>
														<span className="text-xs leading-none">Brutto</span>
													</div>
												</div>
											) : (
												<div className="mt-1 flex flex-col gap-1 text-secondary-700">
													<span className="text-2xl font-bold leading-none">Gratis</span>
												</div>
											)}
										</div>

										<div>
											<p className="text-sm font-bold tracking-wide text-secondary-700">
												Tag & Uhrzeit
											</p>
											<ul className="mt-1 flex flex-col gap-1 text-base text-secondary-700 lg:flex-col">
												{dates.map((date) => (
													<li key={date.format()} className="flex items-start xl:items-center">
														<FaRegClock className="hidden xl:visible" />
														<p className="flex flex-row gap-1">
															<span>{date.format('dd, DD.MM.YY')}</span>
															<span>
																{dayjs(event.start).format('HH:mm')} -{' '}
																{dayjs(event.end).format('HH:mm')}
															</span>
														</p>
													</li>
												))}
											</ul>
										</div>

										<div>
											<p className="text-sm font-bold tracking-wide text-secondary-700">
												Veranstaltungsort
											</p>
											<div className="mt-1 rounded-lg bg-white text-secondary-700">
												<div className="flex items-center gap-1">
													<FaGlobe className="hidden xl:visible" />
													<p className="text-base font-semibold">Live-Online</p>
												</div>
											</div>
										</div>
									</div>
									<div>
										<p className="text-lg font-bold tracking-wide text-secondary-700">Inhalt</p>
										<MarkdownRaw
											className={
												'prose-lg mt-1 text-secondary-700 prose-headings:text-secondary-700 prose-a:text-primary-500 prose-a:hover:text-primary-700 prose-blockquote:text-secondary-700 prose-strong:text-secondary-700 prose-li:mb-0 prose-li:mt-1'
											}
										>
											{event.content}
										</MarkdownRaw>
									</div>
								</div>
							</AccordionPanel>
						</AccordionItem>
					);
				})}
			</Accordion>

			{!isShowAll && events.length > MAX_ITEMS_TO_SHOW && (
				<div className="flex justify-center">
					<Button
						onClick={handleClickShowAll}
						variant="outline"
						size="sm"
						rounded="full"
						className="mt-8"
					>
						Alle {events.length} Veranstaltungen anzeigen
					</Button>
				</div>
			)}
			{selectedEvent && (
				<Drawer
					title={`Anmeldung für ${selectedEvent.title}`}
					isOpen={isOpen}
					onClose={() => {
						setSelectedAppointment(null);
						onClose();
					}}
				>
					<DigitalizationAttendeesForm
						event={selectedEvent}
						handleSubmit={handleAttend}
						isLoading={isSending}
						className="mt-3"
					/>
				</Drawer>
			)}
		</div>
	);
}

function createEmailMessage(args: {
	event: Digitalization['events'][number];
	attendees: Attendee[];
}) {
	const { event, attendees } = args;

	const formattedDate = dayjs(event.start).format('DD.MM.YYYY');
	const formattedStartTime = dayjs(event.start).format('HH:mm');
	const formattedEndTime = dayjs(event.end).format('HH:mm');

	const attendeesList = attendees
		.map((attendee, index) => {
			return `${index + 1}. ${attendee.anrede} ${attendee.vorname} ${attendee.nachname}
   E-Mail: ${attendee.email}`;
		})
		.join('\n\n');

	return `Neue Anmeldung für Online-Veranstaltung

Veranstaltung: ${event.title}
Datum: ${formattedDate}
Uhrzeit: ${formattedStartTime} - ${formattedEndTime} Uhr
Format: Live-Online

Angemeldete Teilnehmer:
${attendeesList}

--
Diese E-Mail wurde automatisch generiert.`;
}

export const pageQuery = graphql`
	query DigitalizationTemplate($id: String!) {
		digitalizationPage: markdownRemark(id: { eq: $id }) {
			id
			frontmatter {
				seo {
					title
					description
				}
				title
				heroImage {
					title
					alt
					image {
						publicURL
						extension
						childImageSharp {
							gatsbyImageData(layout: CONSTRAINED, width: 1540)
							# Add optimized image for SEO/OG
							seoImage: gatsbyImageData(
								layout: FIXED
								width: 1200
								height: 630
								transformOptions: { cropFocus: CENTER }
							)
						}
					}
				}
				uuid
				slug
				intro
				sections {
					type
					title
					text
					subtitle
					horizontal
					link {
						title
						urlPath
					}
					links {
						title
						urlPath
					}
					cards {
						icon

						image {
							title
							alt
							image {
								publicURL
								extension
								childImageSharp {
									gatsbyImageData(layout: FIXED, height: 95)
								}
							}
						}
						title
						text
						link {
							title
							urlPath
						}
					}
					intro
					items {
						title
						text
					}
					gridTitle
					maxColumns
					gridItems {
						text
						icon
						iconRotation
					}
					embeds {
						title
						url
					}
					backgroundColor
					reviews {
						name
						text
						rating
						url
						avatarUrl
					}
					steps {
						title
						text
					}
					numbered
					designElementPosition
					points {
						title
						text
					}
				}
				hideEvents
				events {
					uuid
					start
					end
					price
					title
					content
				}
			}
		}
	}
`;
